import {
  CommerceSearchParameters,
  Product,
} from "@coveo/headless-react/ssr-commerce";
import { useAnalytics } from "../providers/Analytic/Analytic.provider";
import {
  BaseProductWithPriceOptions,
  PriceOption,
  BaseProductWithChildren,
} from "@greystar/ui";
import { useEffect, useRef, useState } from "react";
import { placesTextSearch } from "../data/google/places-text-search";
import { getPdpUrlById } from "../helpers/getPdpUrlById";
import { formatCurrency } from "../pdp/pdp-helper";
import { seoHost } from "../seo/seo-helper";
import { BoundingBox, Coordinate } from "../types/map-types";
import {
  SearchBoxResult,
  SearchParams,
  SearchParamsConfig,
} from "../types/search-types";
import { SearchMetaBuilderConfig } from "../types/seo-types";
import { paramsResolver } from "./params-resolver";
import { getSiteBaseUrl } from "../helpers/site-helper";
import { GetHighlightsMappingStatic } from "../helpers/highlightHelpers";

interface UseSearchSuggestionsParams<T> {
  searchBoxState: {
    suggestions: Array<{ rawValue: string }>;
    value: string;
  };
  controllerInstantProducts: {
    updateQuery: (query: string) => void;
  };
  setItems: (items: T[]) => void;
}

type CreateInputChangeHandlerParams = {
  setSearchInputValue: (val: string) => void;
  setInputIcon: (icon: "Search" | "CloseAlt") => void;
  onSearchBoxInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setLocationValue?: (val: string) => void; // Optional
  setIsInputFocused: (focused: boolean) => void;
};

type CreateModalInputChangeHandlerParams = {
  setModalInputValue: (val: string) => void;
  setInputIcon: (icon: "Search" | "CloseAlt") => void;
  onSearchBoxInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setIsInputFocused: (focused: boolean) => void;
};

type HandleSearchResultClickParams = {
  event: MouseEvent;
  searchInputValue: string;
  pushDataLayerEvent: (event: string, data: Record<string, any>) => void;
};

type CreateInputFocusHandlerParams = {
  setIsInputFocused: (focused: boolean) => void;
  setFocus?: (target: string) => void;
  locationValue?: string;
  setLocationValue?: (val: string) => void;
  handleInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setInputIcon: (icon: "Search" | "CloseAlt") => void;
  inputId?: string;
};

type CreateModalInputFocusHandlerParams = {
  setIsInputFocused: (focused: boolean) => void;
  setFocus: (target: "modal") => void;
  setInputIcon: (icon: "Search" | "CloseAlt") => void;
};

type UseTrackSearchModalOpenParams = {
  variant: string | undefined;
  trackEvent: (event: string, payload: Record<string, any>) => void;
};

type HandleDropdownFocusParams = {
  setIsInputFocused: (val: boolean) => void;
  setInputIcon: (icon: "Search" | "CloseAlt") => void;
  pushDataLayerEvent: (event: string, data: { actionType: string }) => void;
  setSearchInputValue: (val: string) => void;
  setItems: (items: SearchBoxResult[]) => void;
  inputIcon: string | undefined;
  setModalInputValue: (val: string) => void;
  uniqueId?: string;
};

type BuildInputPropsParams = {
  value: string;
  onFocus: React.FocusEventHandler<HTMLInputElement>;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onClick: React.MouseEventHandler<HTMLInputElement>;
  onKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
};

export const extractPropertyId = (input: string): string | null => {
  const Regex = /^(property|unit):\/\/(\d+)/;
  const trimmedInput = input?.trim() ?? "";
  const match = Regex.exec(trimmedInput);
  if (match?.[2]) {
    return match[2];
  } else {
    return null;
  }
};

export function truncateString(str: string | null, maxLength: number) {
  if (typeof str !== "string") return ""; // Handle non-string inputs
  if (!str) return str; // Handle null/undefined/empty

  return str.length > maxLength ? str.substring(0, maxLength) + "..." : str;
}

export function formatCityStateUrl(
  city: string | null,
  state: string | null
): string {
  // Convert to lowercase and replace spaces with hyphens
  const formattedCity = (city ?? "city")
    .toLowerCase()
    .replaceAll("\t", "")
    .replace(/\s+/g, "-");
  const formattedState = (state ?? "state")
    .toLowerCase()
    .replaceAll("\t", "")
    .replace(/\s+/g, "-");

  return `${formattedCity}-${formattedState}`;
}

export const extractSearchBoxResults = (
  products: Product[],
  query: string = ""
): SearchBoxResult[] => {
  if (!products) {
    return [];
  }

  const newItems: SearchBoxResult[] = [];
  const newChilds: SearchBoxResult[] = [];
  if (products.length > 0) {
    products.forEach((product: Product) => {
      const propertyId = extractPropertyId(product.clickUri);
      if (propertyId && product.ec_brand) {
        let childCount = product.children.length;
        if (product.children && product.children.length > 0) {
          product.children.forEach((child) => {
            const propertyName = child.ec_brand ?? "propertyName";
            newChilds.push({
              id: propertyId,
              link: getPdpUrlById(
                extractPropertyId(child.clickUri),
                propertyName,
                child.additionalFields["city"] as string,
                child.additionalFields["state"] as string
              ),
              cityName: truncateString(child.ec_brand, 20),
              stateCode: child.additionalFields["state"] as string,
              communities: child.additionalFields["city"] as string,
            });
          });
        } else {
          childCount = 1;
          const propertyName = product.ec_brand ?? "propertyName";
          newChilds.push({
            id: propertyId,
            link: getPdpUrlById(
              extractPropertyId(product.clickUri),
              propertyName,
              product.additionalFields["city"] as string,
              product.additionalFields["state"] as string
            ),
            cityName: truncateString(product.ec_brand, 20),
            stateCode: product.additionalFields["state"] as string,
            communities: product.additionalFields["city"] as string,
          });
        }

        const stateCode = product.additionalFields["state"] as string;
        const communities = product.additionalFields["city"] as string;
        if (childCount > 0) {
          const city = communities?.toLowerCase();
          const searchTerm = query.toLowerCase();

          if (
            city === searchTerm ||
            searchTerm.split(" or ").some((term) => city?.includes(term))
          ) {
            newItems.push({
              id: propertyId,
              link: `${getSiteBaseUrl("Search")}s/${formatCityStateUrl(communities, stateCode)}`,
              cityName: truncateString(communities, 20),
              stateCode: stateCode,
              communities: `${childCount} Greystar Communities`,
            });
          }
        }
      }
    });
  }

  newItems.push(...newChilds);

  // Use toSorted for immutability and clarity
  const sortedItems = newItems.toSorted((a, b) => {
    const aCount = parseInt(a.communities.replace("Greystar Communities", ""));
    const bCount = parseInt(b.communities.replace("Greystar Communities", ""));
    if (aCount < bCount) return 1;
    if (bCount < aCount) return -1;
    return 0;
  });

  return sortedItems.map((item) => ({
    ...item,
  }));
};

export const parseSearchUrlRequestPath = async (
  requestPath: string | string[],
  config: SearchParamsConfig,
  referer: string
): Promise<SearchParams> => {
  const searchParams: SearchParams = {
    googlePlacesData: null,
    requestPath: requestPath,
    navigatorLocation: null,
    bedrooms: null,
    bathrooms: null,
    priceMin: null,
    priceMax: null,
    buildingTypes: null,
    highlights: null,
  };

  if (requestPath && Array.isArray(requestPath) && requestPath.length > 0) {
    // if the first url segment is a locale, remove it from the requestPath array

    const regex = /^[A-Za-z]{2}(-[A-Za-z]{2})?$/;
    const match = regex.exec(requestPath[0]);

    if (match && requestPath.length > 1) {
      requestPath.shift();
    }

    // search urls are prefixed with "s" for routing purposes but this has no use here and should be ignored

    if (requestPath[0] === "s" && requestPath.length > 1) {
      requestPath.shift();
    }

    const getSearchParams = config.doGooglePlacesSearch
      ? () => parseLocation(requestPath[0], searchParams, referer)
      : () => Promise.resolve(searchParams);

    const updatedSearchParams = await getSearchParams();

    await paramsResolver.extract(
      requestPath.length > 1 ? requestPath.slice(1) : [],
      updatedSearchParams,
      config
    );
  }

  return searchParams;
};

export const parseLocation = async (
  segment: string,
  searchParams: SearchParams,
  referer: string
): Promise<SearchParams> => {
  const placesData = await placesTextSearch(segment, referer);

  if (placesData?.places && placesData.places.length > 0) {
    searchParams.googlePlacesData = placesData.places[0];
  }

  return searchParams;
};

export const buildSearchMeta = async (
  searchParams: SearchParams,
  config: SearchMetaBuilderConfig
): Promise<SearchParams> => {
  searchParams.meta = {
    metaTitle: await buildSearchMetaTitle(searchParams),
    metaDescription: await buildSearchMetaDescription(searchParams, config),
    metaCanonicalLink: buildSearchCanonicalUrl(searchParams),
    metaOgTitle: await buildSearchMetaOgTitle(searchParams),
    metaOgType: await buildSearchMetaOgType(searchParams, config),
    metaOgDescription: await buildSearchMetaOgDescription(searchParams, config),
    metaOgImage: await buildSearchMetaOgImage(searchParams, config),
    metaOgUrl: buildSearchCanonicalUrl(searchParams),
  };

  return searchParams;
};

export const buildSearchMetaTitle = async (
  searchParams: SearchParams
): Promise<string> => {
  const HIGHLIGHTS_MAPPING = GetHighlightsMappingStatic();

  const filters: string[] = [];

  let price = "";
  if (searchParams.priceMin && searchParams.priceMax) {
    price = `$${searchParams.priceMin}-${searchParams.priceMax}`;
  } else if (searchParams.priceMin) {
    price = `$${searchParams.priceMin}+`;
  } else if (searchParams.priceMax) {
    price = `<$${searchParams.priceMax}`;
  }

  if (price !== "") {
    filters.push(price);
  }

  const getBedroomsText = (): string => {
    if (!searchParams.bedrooms?.length) return "";
    return searchParams.bedrooms[0] === "0"
      ? "Studio"
      : `${searchParams.bedrooms[0]} Bedroom`;
  };

  if (searchParams.bedrooms?.length) {
    filters.push(getBedroomsText());
  }

  if (searchParams.buildingTypes?.length) {
    filters.push(
      searchParams.buildingTypes
        .map((type) =>
          type
            .split("-")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join("-")
        )
        .join(", ")
    );
  }

  if (searchParams.highlights?.length) {
    filters.push(
      searchParams.highlights
        .map((highlight) => {
          return (
            HIGHLIGHTS_MAPPING[highlight as keyof typeof HIGHLIGHTS_MAPPING]
              ?.text ?? highlight
          );
        })
        .join(", ")
    );
  }

  const filtersSegment = filters.length ? `${filters.join(", ")} ` : "";

  const location =
    searchParams.googlePlacesData?.formattedAddress ??
    formatLocationForMetaTitle(searchParams.requestPath);

  return `${filtersSegment}Apartments for Rent in ${location} | Greystar`;
};

export const buildSearchMetaTitleOnUpdate = (
  searchParams: SearchParams | null,
  commerceSearchParameters: CommerceSearchParameters
): string => {
  const HIGHLIGHTS_MAPPING = GetHighlightsMappingStatic();

  const filters: string[] = [];

  let price = "";

  if (
    commerceSearchParameters.mnf?.ec_price &&
    commerceSearchParameters.mnf?.ec_price.length > 0
  ) {
    price = `$${commerceSearchParameters.mnf.ec_price[0].start}-${commerceSearchParameters.mnf.ec_price[0].end}`;
  }

  if (price !== "") {
    filters.push(price);
  }

  const getBedroomsText = (): string => {
    return commerceSearchParameters.f?.bedrooms_string[0] === "0"
      ? "Studio"
      : `${commerceSearchParameters.f?.bedrooms_string[0]} Bedroom`;
  };

  if (
    commerceSearchParameters.f?.bedrooms_string &&
    commerceSearchParameters.f?.bedrooms_string.length > 0
  ) {
    filters.push(getBedroomsText());
  }

  if (
    commerceSearchParameters.f?.building_type &&
    commerceSearchParameters.f?.building_type.length > 0
  ) {
    filters.push(
      commerceSearchParameters.f?.building_type
        .map((type) =>
          type
            .split("-")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join("-")
        )
        .join(", ")
    );
  }

  if (
    commerceSearchParameters.f?.highlights &&
    commerceSearchParameters.f?.highlights.length > 0
  ) {
    filters.push(
      commerceSearchParameters.f?.highlights
        .map((highlight) => {
          return (
            HIGHLIGHTS_MAPPING[highlight as keyof typeof HIGHLIGHTS_MAPPING]
              ?.text ?? highlight
          );
        })
        .join(", ")
    );
  }

  const filtersSegment = filters.length ? `${filters.join(", ")} ` : "";

  const location =
    searchParams?.googlePlacesData?.formattedAddress ??
    formatLocationForMetaTitle(searchParams?.requestPath);

  return `${filtersSegment}Apartments for Rent in ${location} | Greystar`;
};

function formatLocationForMetaTitle(
  tags: string[] | string | undefined
): string | null {
  if (!tags?.length) return null;

  const firstTag = tags[0];

  return firstTag
    .split("-")
    .map((word) => {
      if (word.length === 2) return word.toUpperCase();
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
}

export const buildSearchMetaDescription = async (
  searchParams: SearchParams,
  config: SearchMetaBuilderConfig
): Promise<string> => {
  let description = config.metaDescriptionTemplate;

  searchParams.googlePlacesData?.addressComponents.forEach(
    (addressComponent) => {
      if (addressComponent.types.length) {
        addressComponent.types.forEach((addressType) => {
          description = description.replace(
            `{${addressType}}`,
            addressComponent.longText
          );
        });
      }
    }
  );

  return description;
};

export const buildSearchMetaOgTitle = async (
  searchParams: SearchParams
): Promise<string> => {
  return await buildSearchMetaTitle(searchParams);
};

export const buildSearchMetaOgType = async (
  _searchParams: SearchParams,
  config: SearchMetaBuilderConfig
): Promise<string> => {
  return config.ogType;
};

export const buildSearchMetaOgDescription = async (
  _searchParams: SearchParams,
  config: SearchMetaBuilderConfig
): Promise<string> => {
  return `${config.ogDescriptionTemplate}`;
};

export const buildSearchMetaOgImage = async (
  _searchParams: SearchParams,
  _config: SearchMetaBuilderConfig
): Promise<string> => {
  return ``;
};

export const buildSearchCanonicalUrl = (searchParams: SearchParams): string => {
  if (!searchParams.navigatorLocation) {
    return `${seoHost}/s/us`;
  }

  const url = new URL(`${seoHost}${searchParams.navigatorLocation}`);

  let relativeUrl = url.pathname.split("/").slice(0, 3).join("/");

  if (searchParams.bedrooms?.length) {
    relativeUrl += `/${searchParams.bedrooms[0]}-beds`;
  }

  return `${seoHost}${relativeUrl}`;
};

export const inBoundingBox = async (
  bottomLeft: Coordinate,
  topRight: Coordinate,
  point: Coordinate
): Promise<boolean> => {
  let isLongInRange = false;
  let isLatiInRange = false;
  isLongInRange = point.lng >= bottomLeft.lng && point.lng <= topRight.lng;
  isLatiInRange = point.lat >= bottomLeft.lat && point.lat <= topRight.lat;
  return isLongInRange && isLatiInRange;
};

export const setFocus = (type: string, id: string = "Search") => {
  let selector = "";

  if (type === "dropdown") {
    selector = `input[id="${id}"]`;
  } else {
    const modalId = id === "Search" ? "modalSearch" : `modalSearch-${id}`;
    selector = `input[id="${modalId}"]`;
  }

  const input = document.querySelector(selector);

  if (input instanceof HTMLInputElement) {
    input.focus();
  }
};

export const getLocationPathFromUrl = (url: string): string => {
  const path = url
    .split("?")[0]
    .split("/")
    .filter(Boolean)
    .slice(0, 2)
    .join("/");
  return `/${path}`;
};

export const getLocationFromUrl = (url: string): string => {
  return url.split("?")[0].split("/").filter(Boolean)[1];
};

export const getCenterPointFromCluster = (
  cluster: Coordinate[]
): Coordinate => {
  if (!cluster || cluster.length === 0) {
    return { lat: 0, lng: 0 };
  }

  const sum = cluster.reduce(
    (acc, coord) => ({
      lat: acc.lat + coord.lat,
      lng: acc.lng + coord.lng,
    }),
    { lat: 0, lng: 0 }
  );

  return {
    lat: sum.lat / cluster.length,
    lng: sum.lng / cluster.length,
  };
};

export const getBoundingBoxFromCluster = (
  cluster: Coordinate[]
): BoundingBox => {
  if (!cluster || cluster.length === 0) {
    return {
      sw: { lat: 0, lng: 0 },
      ne: { lat: 0, lng: 0 },
    };
  }

  const bounds = cluster.reduce(
    (acc, coord) => ({
      sw: {
        lat: Math.min(acc.sw.lat, coord.lat),
        lng: Math.min(acc.sw.lng, coord.lng),
      },
      ne: {
        lat: Math.max(acc.ne.lat, coord.lat),
        lng: Math.max(acc.ne.lng, coord.lng),
      },
    }),
    {
      sw: { lat: cluster[0].lat, lng: cluster[0].lng },
      ne: { lat: cluster[0].lat, lng: cluster[0].lng },
    }
  );

  return bounds;
};

export const transformPropertiesBase = (
  properties: BaseProductWithChildren[]
): BaseProductWithPriceOptions[] => {
  return properties.map((property): BaseProductWithPriceOptions => {
    const priceOptions: PriceOption[] = [];

    if (
      (!property.children || !property.children.length) &&
      property.ec_price &&
      property.additionalFields.required_monthly_fees_min != null &&
      property.additionalFields.required_monthly_fees_max != null
    ) {
      priceOptions.push({
        type: "tmlp",
        rawPrice: property.ec_price,
        price: formatCurrency(property.ec_price, "$"),
      });
      if (property.additionalFields["min_price"]) {
        priceOptions.push({
          type: "baseRent",
          rawPrice: property.additionalFields["min_price"] as number,
          price: formatCurrency(
            property.additionalFields["min_price"] as number,
            "$"
          ),
        });
      }
    } else if (property.children && property.children.length) {
      const lowestTmlp = property.children.reduce((min, curr) => {
        if (!curr.ec_price) return min;
        if (!min.ec_price || curr.ec_price < min.ec_price) return curr;
        return min;
      }, property.children[0]);

      if (
        lowestTmlp?.ec_price &&
        property.additionalFields.required_monthly_fees_min != null &&
        property.additionalFields.required_monthly_fees_max != null
      ) {
        priceOptions.push({
          type: "tmlp",
          rawPrice: lowestTmlp.ec_price,
          price: formatCurrency(lowestTmlp.ec_price, "$"),
        });

        const lowestBaseRent = property.children.reduce((min, curr) => {
          if (!curr.additionalFields["min_price"]) return min;
          if (
            !min.additionalFields["min_price"] ||
            curr.additionalFields["min_price"] <
              min.additionalFields["min_price"]
          )
            return curr;
          return min;
        }, property.children[0]);

        if (lowestBaseRent?.additionalFields["min_price"]) {
          priceOptions.push({
            type: "baseRent",
            rawPrice: lowestBaseRent.additionalFields["min_price"] as number,
            price: formatCurrency(
              lowestBaseRent.additionalFields["min_price"] as number,
              "$"
            ),
          });
        }
      }
    }
    return {
      ...property,
      priceOptions: [...priceOptions],
      children: property.children ?? [],
      totalNumberOfChildren: property.totalNumberOfChildren || 0,
    };
  });
};

export type SearchAnalyticsEventData = Partial<{
  actionType: string;
  search_selected: string;
  destination_url: string;
  search_category: string;
  property_id: string;
  property_name: string;
  state?: string;
  property_location?: string;
  click_text?: string;
  search_term?: string;
  module_name?: string;
  inpage_location?: string;
  click_type?: string;
}>;

// Shared analytics function
export const useSearchAnalytics = () => {
  const analytics = useAnalytics();

  const pushDataLayerEvent = (
    event: string,
    additionalData: SearchAnalyticsEventData = {}
  ) => {
    const eventData = {
      event: event,
      action_type: additionalData.actionType,
      state: additionalData.state ?? "",
      property_location: additionalData.search_selected ?? "",
      property_id: additionalData.property_id ?? "",
      property_name: additionalData.property_name ?? "",
      search_category: additionalData.search_category ?? "",
      search_selected: additionalData.search_selected ?? "",
      module_name: "search",
      inpage_location: "top",
      click_text: additionalData.click_text ?? "",
      click_type: "internal",
      destination_url: additionalData.destination_url ?? "",
      search_term: additionalData.search_term ?? "",
    };

    analytics.trackEvent(event, eventData);
  };

  return { pushDataLayerEvent };
};

// Shared click handler for search results
export const useSearchResultClickHandler = (searchInputValue: string) => {
  const { pushDataLayerEvent } = useSearchAnalytics();

  useEffect(() => {
    const handleSearchResultClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const searchResultLink = target.closest("a.search-result");
      if (searchResultLink) {
        if (searchInputValue.trim().length === 0) return;
        const cityName =
          searchResultLink
            .querySelector(".search-result--city-name")
            ?.textContent?.trim() ?? "";
        const stateCode =
          searchResultLink
            .querySelector(".search-result--state-code")
            ?.textContent?.trim() ?? "";
        const communities =
          searchResultLink
            .querySelector(".search-result--communities")
            ?.textContent?.trim() ?? "";
        const url = searchResultLink.getAttribute("href") ?? "";

        // Determine if this is a city search or property search
        const isCitySearch = communities.includes("Greystar Communities");
        const searchCategory = isCitySearch ? "city" : "property";
        const propertyName = isCitySearch ? "" : cityName;
        pushDataLayerEvent("property_search_completed", {
          actionType: "completed",
          search_selected: `${cityName}, ${stateCode}`,
          destination_url: url,
          search_category: searchCategory,
          property_id: "",
          property_name: propertyName,
          state: stateCode,
          property_location: cityName,
          click_text: `${cityName} ${stateCode}, ${communities}`,
          search_term: searchInputValue,
        });
      }
    };

    document.addEventListener("click", handleSearchResultClick);
    return () => {
      document.removeEventListener("click", handleSearchResultClick);
    };
  }, [searchInputValue]);
};

// Shared key down handler for Enter key
export const handleSearchKeyDown =
  (items: any[]) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    const mobileReturnKeys = ["Enter", "Go", "Search", "Done", "Next", "enter"];
    if (mobileReturnKeys.includes(e.key) || mobileReturnKeys.includes(e.code)) {
      e.preventDefault();

      if (items.length > 0) {
        const selectors = [
          '[data-first-result="true"]',
          ".search-dropdown .search-results a",
          ".search-results a",
          '[role="option"]',
        ];

        const firstResult = selectors.reduce(
          (found, selector) => {
            return found || document.querySelector(selector);
          },
          null as HTMLElement | null
        );

        if (firstResult) {
          firstResult.click();
        }
      }
    }
  };

// Shared body click handler for dropdown
export const useDropdownFocusHandler = (
  setIsInputFocused: (value: boolean) => void,
  setInputIcon: (value: "Search" | "CloseAlt" | undefined) => void,
  setSearchInputValue: (value: string) => void,
  setModalInputValue: (value: string) => void,
  setItems: (items: SearchBoxResult[]) => void,
  includeTestId: boolean = true,
  inputId?: string
) => {
  useEffect(() => {
    const getInputWrapper = (id: string) => {
      const inputEl = document.getElementById(id);
      return inputEl?.closest(".search-dropdown, .search-modal--input") ?? null;
    };

    const isElementInsideWrapper = (
      path: EventTarget[],
      wrapper: Element | null
    ): boolean => {
      if (!wrapper) return false;
      return path.some(
        (el) => el instanceof HTMLElement && wrapper.contains(el)
      );
    };

    const isClickInsideAnyModalWrapper = (
      path: EventTarget[],
      selector: string
    ): boolean => {
      const wrappers = document.querySelectorAll(selector);
      return Array.from(wrappers).some((wrapper) =>
        isElementInsideWrapper(path, wrapper)
      );
    };

    const handleBodyClick = (e: MouseEvent) => {
      const path = e.composedPath();
      const id = inputId ?? "Search";
      const wrapper = getInputWrapper(id);

      const isInside =
        isElementInsideWrapper(path, wrapper) ||
        isClickInsideAnyModalWrapper(path, ".search-modal--input");

      if (isInside) {
        setIsInputFocused(true);
      } else {
        setIsInputFocused(false);
        setInputIcon("Search");
      }
    };

    document.addEventListener("click", handleBodyClick);
    return () => {
      document.removeEventListener("click", handleBodyClick);
    };
  }, [
    includeTestId,
    inputId,
    setInputIcon,
    setIsInputFocused,
    setItems,
    setModalInputValue,
    setSearchInputValue,
  ]);
};

// Shared controller subscription
export const useControllerSubscription = (
  controller: any,
  setState: (state: any) => void
) => {
  useEffect(() => {
    const unsubscribe = controller.subscribe(() => setState(controller.state));
    return () => unsubscribe();
  }, [controller, setState]);
};

export const useDropdownFocusWithButton = (
  setIsInputFocused: (value: boolean) => void
) => {
  setIsInputFocused(true);
  setFocus("dropdown");
};

// Add to your existing search utilities
export interface SearchInputHandlerParams {
  controllerSearchBox: {
    clear: () => void;
    updateText: (text: string) => void;
    state: { value: string };
  };
  controllerInstantProducts: {
    updateQuery: (query: string) => void;
  };
  setItems: (items: SearchBoxResult[]) => void;
  minQueryLength?: number;
}

export const createSearchInputHandler = ({
  controllerSearchBox,
  controllerInstantProducts,
  setItems,
  minQueryLength = 2,
}: SearchInputHandlerParams) => {
  return (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value.length < minQueryLength) {
      setItems([]);
      return;
    }

    controllerSearchBox.updateText(value);
    controllerInstantProducts.updateQuery(value);
  };
};

export const useSearchSuggestions = <T>({
  searchBoxState,
  controllerInstantProducts,
  setItems,
}: UseSearchSuggestionsParams<T>) => {
  const [lastSuggestion, setLastSuggestion] = useState<string>("");

  useEffect(() => {
    if (searchBoxState.suggestions[0]) {
      const currentSuggestion = getQuerySuggestString(
        searchBoxState.value,
        searchBoxState.suggestions
      );
      if (lastSuggestion != currentSuggestion) {
        setItems([]);
        setLastSuggestion(currentSuggestion);
      }
      controllerInstantProducts?.updateQuery(currentSuggestion);
    } else {
      controllerInstantProducts?.updateQuery(searchBoxState.value);
    }
  }, [
    searchBoxState.suggestions,
    controllerInstantProducts,
    searchBoxState.value,
    lastSuggestion,
  ]);

  return { lastSuggestion };
};

export const scrollToElement = (
  className: string,
  offset: number = 66
): void => {
  const elements = document.getElementsByClassName(className);
  if (elements.length > 0) {
    const element = elements[0] as HTMLElement;
    const top =
      element.getBoundingClientRect().top +
      (document.documentElement.scrollTop || document.body.scrollTop) -
      offset;
    document.documentElement.scrollTo({
      top,
    });
  }
};

export const clampTotalPages = (totalPages: number, maxPages = 50): number =>
  totalPages > maxPages ? maxPages : totalPages;

export const clampTotalEntries = (
  totalPages: number,
  pageSize: number,
  totalEntries: number,
  maxPages = 50
): number => (totalPages > maxPages ? pageSize * maxPages : totalEntries);

export const createInputChangeHandler = ({
  setSearchInputValue,
  setInputIcon,
  onSearchBoxInputChange,
  setLocationValue,
  setIsInputFocused,
}: CreateInputChangeHandlerParams) => {
  return (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInputValue(value);
    onSearchBoxInputChange(e);
    setIsInputFocused(true);
    if (setLocationValue) {
      setLocationValue(value);
    }

    setInputIcon(value.length > 0 ? "CloseAlt" : "Search");
  };
};

export const createModalInputChangeHandler = ({
  setModalInputValue,
  setInputIcon,
  onSearchBoxInputChange,
  setIsInputFocused,
}: CreateModalInputChangeHandlerParams) => {
  return (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setModalInputValue(value);
    onSearchBoxInputChange(e);
    setIsInputFocused(true);

    setInputIcon(value.length > 0 ? "CloseAlt" : "Search");
  };
};

export const handleSearchResultClick = ({
  event,
  searchInputValue,
  pushDataLayerEvent,
}: HandleSearchResultClickParams) => {
  const target = event.target as HTMLElement;
  const searchResultLink = target.closest("a.search-result");

  if (searchResultLink) {
    const cityName =
      searchResultLink
        .querySelector(".search-result--city-name")
        ?.textContent?.trim() ?? "";
    const stateCode =
      searchResultLink
        .querySelector(".search-result--state-code")
        ?.textContent?.trim() ?? "";
    const communities =
      searchResultLink
        .querySelector(".search-result--communities")
        ?.textContent?.trim() ?? "";

    const url = searchResultLink.getAttribute("href") ?? "";
    const propertyRegex = /\/p_(\d+)/;
    const match = propertyRegex.exec(url);
    const property_id = match ? match[1] : "";

    const isCitySearch = communities.includes("Greystar Communities");
    const searchCategory = isCitySearch ? "city" : "property";
    const propertyName = isCitySearch ? "" : cityName;

    pushDataLayerEvent("property_search_completed", {
      actionType: "completed",
      search_selected: `${cityName}, ${stateCode}`,
      destination_url: url,
      search_category: searchCategory,
      property_id,
      property_name: propertyName,
      state: stateCode,
      property_location: `${cityName} ${stateCode}`,
      click_text: `${cityName} ${stateCode}, ${communities}`,
      search_term: searchInputValue,
    });
  }
};

export const createInputFocusHandler = ({
  setIsInputFocused,
  locationValue,
  setLocationValue,
  handleInputChange,
  setInputIcon,
  inputId,
}: CreateInputFocusHandlerParams) => {
  return (e: React.FocusEvent<HTMLInputElement>) => {
    setFocus("dropdown", inputId ?? "Search");
    setIsInputFocused(true);

    if (e.target.value.length > 0) {
      setInputIcon("CloseAlt");
    }

    if (locationValue && setLocationValue) {
      const [city] = locationValue.split(",").map((v) => v.trim());

      if (city) {
        setLocationValue(city);

        if (handleInputChange) {
          const syntheticEvent = {
            target: { value: city },
          } as React.ChangeEvent<HTMLInputElement>;

          handleInputChange(syntheticEvent);
        }
      }
    }
  };
};

export const createInputModalFocusHandler = ({
  setIsInputFocused,
  setFocus,
  setInputIcon,
}: CreateModalInputFocusHandlerParams) => {
  return (e: React.FocusEvent<HTMLInputElement>) => {
    setIsInputFocused(true);
    setFocus("modal");
    if (e.target.value.length > 0) {
      setInputIcon("CloseAlt");
    }
  };
};

export const useTrackSearchModalOpen = ({
  variant,
  trackEvent,
}: UseTrackSearchModalOpenParams) => {
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    if (variant === "modal" && !hasTrackedRef.current) {
      trackEvent("property_search_open", {
        event: "property_search_open",
        actionType: "search model open",
        module_name: "search",
        inpage_location: "top",
        click_text: "Search for homes",
        click_type: "internal",
        destination_url: "",
      });
      trackEvent("property_search_entry", {
        actionType: "entry",
      });
      hasTrackedRef.current = true;
    }
  }, [variant, trackEvent]);
};

export const handleDropdownFocusWithButton = ({
  setIsInputFocused,
  setInputIcon,
  pushDataLayerEvent,
  setSearchInputValue,
  setItems,
  inputIcon,
  setModalInputValue,
  uniqueId,
}: HandleDropdownFocusParams) => {
  setIsInputFocused(true);
  const buildSelector = (baseId: string) => {
    return uniqueId
      ? `input[id="${uniqueId}"]`
      : `input[data-testid="${baseId}"]`;
  };

  const handleInputElement = (selector: string, isMobile: boolean = false) => {
    const input = document.querySelector(selector);
    if (!(input instanceof HTMLInputElement)) return;

    input.focus();
    const value = input.value.trim();

    if (value.length === 0) {
      setInputIcon("Search");

      setFocus("dropdown", uniqueId ?? "Search");

      if (isMobile) {
        setModalInputValue("");
        setItems([]);
      }

      return;
    }

    // For value.length > 0
    if (isMobile) {
      setInputIcon("Search");
      setModalInputValue("");
      setItems([]);
    } else if (inputIcon === "CloseAlt") {
      setSearchInputValue("");
      setItems([]);
      setInputIcon("Search");
    } else {
      setInputIcon("CloseAlt");
    }
  };

  handleInputElement(buildSelector("search-input"));
  handleInputElement('input[data-testid="search-input-mobile"]', true);

  pushDataLayerEvent("property_search_completed", {
    actionType: "completed",
  });
};

// Track search entry click
export const handleInputClick = (
  pushDataLayerEvent: (event: string, data: { actionType: string }) => void
) => {
  pushDataLayerEvent("property_search_entry", {
    actionType: "entry",
  });
};

export const buildCommonInputProps = ({
  value,
  onFocus,
  onChange,
  onClick,
  onKeyDown,
  onBlur,
}: BuildInputPropsParams) => ({
  value,
  onFocus,
  onChange,
  onClick,
  onKeyDown,
  ...(onBlur && { onBlur }),
});

export const getQuerySuggestString = (
  query: string,
  suggestions: Array<{ rawValue: string }>
) => {
  const substrLength = suggestions[0]?.rawValue.length ?? 0;
  if (substrLength === 0) {
    return "";
  }

  const results: string[] = [];
  suggestions.forEach((suggestion, index) => {
    if (index === 0 || suggestion.rawValue.length <= substrLength) {
      results.push(suggestion.rawValue);
    }
  });

  if (!results.some((term) => query?.includes(term))) {
    results.push(query);
  }

  return results.join(" OR ");
};
